import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useGridCopyMenu } from './gridCommon';
import DateInput from './DateInput';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ClientSideRowModelModule,
  type ColDef,
  ModuleRegistry,
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import { callAxiosGet } from '../tmslib/src/util/axiosutils';
import { useMessageState } from '../tmslib/src/context/MessageContext';
import { getMainAxios } from '../utils/util';

ModuleRegistry.registerModules([AllCommunityModule, ClientSideRowModelModule]);

interface ReviewAccount {
  id: number;
  userId: number;
  userNm?: string;
  accountNo: string;
  htsId?: string;
  isReal: boolean;
  isActive: boolean;
  initD: string;
  initAmt: number;
}

/**
 * 일별 손익 집계 1행 (main.kispfpnl) — fdscronjob 의 GenPfBSJob 이 5분마다 채운다.
 *
 * **위 '일별 성과' 그리드와 대조하라고 따로 둔 표다.**
 *   일별 성과 : kispfbs(KIS 잔고)의 순자산 증감 기준
 *   손익 집계 : kispffill(체결)에서 **우리가 직접** 계산한 값 + KIS 값 + 그 차이
 *
 * 두 표가 같은 날 같은 숫자를 보이면 양쪽 다 믿을 수 있다.
 * KIS 모의투자가 일손익(asst_icdc_amt)을 0 으로 주던 사고(2026-10-07) 때문에
 * KIS 값 하나만 보지 않는다.
 */
interface PnlRow {
  htsId: string; id: number; pfId: number; d: string;
  realized: number; buyAmt: number; sellAmt: number;
  buyQty: number; sellQty: number; nFill: number; nSym: number;
  fee: number; tax: number; cost: number;
  net: number; unrealized: number; total: number;
  nav?: number | null; prevNav?: number | null;
  navDiff?: number | null; diffCheck?: number | null;
  kisDiffAmt?: number | null; kisGap?: number | null;
  flat: boolean; retPct?: number | null; updAt?: string | null;
}

interface DailyRow {
  d: string;
  na: number;
  prevNa: number;
  dailyRet: number;
  cumRet: number;
  diffAmt: number;
  totEvluAmt: number;
  stkEvluAmt: number;
  bSumAmt: number;
  evluSumAmt: number;
  prftSumAmt: number;
  fee?: number;       // 수수료 — kispfpos 집계 (sim 만 값 있음)
  tax?: number;       // 거래세 — 동상
  texAmt?: number;    // 거래비용(수수료+거래세) — 당일
  texAmtYd?: number;  // 거래비용 — 전일
  // 당일매매 손익 (2026-10-07) — 체결 기준 매도대금 − 매수대금.
  // 평가손익은 '보유분 미실현'이라 전량청산하는 날은 늘 0 이다. 그때 쓰는 값.
  tradePnl?: number;  // 비용 전
  tradeNet?: number;  // 비용(texAmtYd) 차감 후 = 그날 실제 손익
  flat?: boolean;     // 당일 전량청산 여부(매수수량==매도수량)
  bQty?: number; sQty?: number;
}

/** 일자 클릭 시 하단에 뿌릴 그날 세부 내역 */
interface DetailPos {
  prodId: string; prodNm?: string; pos: number;
  avgPrc?: number; c: number; bAmt: number; evluAmt: number;
  prft: number; evluRt: number; prftKrw?: number;
  bQty: number; sQty: number; fee?: number; tax?: number;
}
interface DetailFill {
  ordT: string; ordNo: number; prodId: string; prodNm?: string;
  lsNm: string; totFillQty: number; avgPrc: number; totFillAmt: number;
  fee?: number; tax?: number;
}
/** 종목별 당일매매 손익 (매도대금 − 매수대금) */
interface DetailTrade {
  prodId: string; prodNm?: string;
  bQty: number; sQty: number; bAmt: number; sAmt: number;
  bAvg: number; sAvg: number; pnl: number; pnlRt: number; flat: boolean;
}
interface DailyDetail {
  d: string; positions: DetailPos[]; fills: DetailFill[];
  tradePnl: DetailTrade[]; tradePnlSum: number; tradeFlat: boolean;
  feeSum: number; taxSum: number;  // API 제공값 (그리드 컬럼으로 표시하므로 제목엔 안 씀)
}

interface Summary {
  days: number;
  firstD: string | null;
  lastD: string | null;
  baseNa: number;
  lastNa: number;
  totalRet: number;
  winDays: number;
  loseDays: number;
  winRate: number;
  avgRet: number;
  maxRet: number;
  minRet: number;
}

// 계좌 정보 + 기간 성과를 합친 통합 행
interface AccountRow extends ReviewAccount {
  days?: number;
  baseNa?: number;
  lastNa?: number;
  totalRet?: number;
  winRate?: number;
  avgRet?: number;
  maxRet?: number;
  minRet?: number;
  loaded?: boolean;
}

// X축 날짜 라벨: YY.MM.DD (전 차트 공통 규칙)
const axisDateLabel = (ms: number) => {
  const dt = new Date(ms);
  const yy = String(dt.getFullYear()).slice(-2);
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yy}.${mm}.${dd}`;
};

// 그리드 제목 — PortfolioOverview 의 기존 관례를 따른다.
//   <h3> 12px/600, 주 그리드 #1e40af / 상세 #6d28d9, 배경·하단선 없음
//   보조설명은 11px/400 #9ca3af 로 제목 옆에 붙인다.
const gridTitle: React.CSSProperties = {
  margin: 0, fontSize: '12px', fontWeight: 600, color: '#1e40af',
};
const gridTitleSub: React.CSSProperties = {
  margin: 0, fontSize: '12px', fontWeight: 600, color: '#6d28d9',
};
const gridHint: React.CSSProperties = {
  marginLeft: 6, fontSize: '11px', fontWeight: 400, color: '#9ca3af',
};
/** 제목 행 — 제목(좌) / 건수·요약(우) */
const titleRow: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between',
  alignItems: 'center', marginBottom: '12px',
};

const card: React.CSSProperties = {
  backgroundColor: 'white', border: '2px solid #3b82f6',
  borderRadius: '8px', padding: '12px 16px', marginBottom: '16px',
};

const sign = (v?: number) =>
  (v ?? 0) > 0 ? '#dc2626' : (v ?? 0) < 0 ? '#2563eb' : '#374151';

const numFmt = (p: any) => p.value != null ? Math.round(p.value).toLocaleString() : '';
const pctFmt = (p: any) => p.value != null ? p.value.toFixed(4) : '';

const PortfolioReview: React.FC = () => {
  const acctCopy = useGridCopyMenu({});
  const dailyCopy = useGridCopyMenu({});
  const { msgBox: m, logger } = useMessageState();
  const axiosInstance = getMainAxios();

  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [pfId, setPfId] = useState<number | null>(null);
  const [rows, setRows] = useState<DailyRow[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  // 일자 클릭 시 하단 세부내역
  const [detailD, setDetailD] = useState<string | null>(null);
  const [detail, setDetail] = useState<DailyDetail | null>(null);
  // 손익 집계(main.kispfpnl) — GenPfBSJob 이 5분마다 채운다. 화면은 읽기만 한다.
  const [pnlRows, setPnlRows] = useState<PnlRow[]>([]);
  const [loading, setLoading] = useState(false);
  const acctGridRef = useRef<AgGridReact>(null);

  const today = new Date().toISOString().slice(0, 10);
  const yearAgo = new Date(Date.now() - 365 * 86400000).toISOString().slice(0, 10);
  const [d0, setD0] = useState(yearAgo);
  const [d1, setD1] = useState(today);

  // 전체 계좌(정지 포함) + 각 계좌의 기간 성과를 한 표로 만든다
  const fetchAll = useCallback(() => {
    setLoading(true);
    callAxiosGet({
      axiosInstance, m, logger,
      url: '/api/v1/portfolio/accounts',
      params: {},
      onSuccess: (data) => {
        const list = ((data as ReviewAccount[]) || []).map(a => ({ ...a } as AccountRow));
        setAccounts(list);
        if (list.length === 0) { setLoading(false); return; }

        // 계좌별 기간 성과를 각각 조회해 행에 합친다
        let remain = list.length;
        list.forEach(acc => {
          callAxiosGet({
            axiosInstance, m, logger,
            url: '/api/v1/portfolio/daily-return',
            params: { id: acc.id, d0, d1 },
            onSuccess: (dd) => {
              const res = dd as { summary: Summary };
              setAccounts(prev => prev.map(p => p.id === acc.id ? {
                ...p, loaded: true,
                days: res?.summary?.days,
                baseNa: res?.summary?.baseNa,
                lastNa: res?.summary?.lastNa,
                totalRet: res?.summary?.totalRet,
                winRate: res?.summary?.winRate,
                avgRet: res?.summary?.avgRet,
                maxRet: res?.summary?.maxRet,
                minRet: res?.summary?.minRet,
              } : p));
              if (--remain <= 0) setLoading(false);
            },
            onError: () => {
              setAccounts(prev => prev.map(p => p.id === acc.id ? { ...p, loaded: true } : p));
              if (--remain <= 0) setLoading(false);
            },
          });
        });
      },
      onError: () => { setAccounts([]); setLoading(false); },
    });
  }, [axiosInstance, m, logger, d0, d1]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // 선택된 계좌의 일별 상세
  const fetchDaily = useCallback((id: number) => {
    callAxiosGet({
      axiosInstance, m, logger,
      url: '/api/v1/portfolio/daily-return',
      params: { id, d0, d1 },
      onSuccess: (data) => {
        const res = data as { summary: Summary; rows: DailyRow[] };
        setRows(res?.rows || []);
        setSummary(res?.summary || null);
      },
      onError: () => { setRows([]); setSummary(null); },
    });
  }, [axiosInstance, m, logger, d0, d1]);

  /**
   * 손익 집계 조회 — 위 '일별 성과' 와 **같은 계좌·같은 기간**을 본다.
   * 기간을 맞춰야 두 표를 나란히 놓고 대조할 수 있다.
   */
  const fetchPnl = useCallback((id: number) => {
    callAxiosGet({
      axiosInstance, m, logger,
      url: '/api/v1/pfbs/pnl',
      params: { pfId: id, startDate: d0, endDate: d1, limit: 400 },
      onSuccess: (data) => setPnlRows(Array.isArray(data) ? data as PnlRow[] : []),
      onError: () => setPnlRows([]),
    });
  }, [axiosInstance, m, logger, d0, d1]);

  useEffect(() => { if (pfId !== null) fetchDaily(pfId); }, [pfId, fetchDaily]);
  useEffect(() => { if (pfId !== null) fetchPnl(pfId); }, [pfId, fetchPnl]);

  // 일자 클릭 → 그날 세부내역(포지션·체결)
  const fetchDetail = useCallback((id: number, d: string) => {
    callAxiosGet({
      axiosInstance, m, logger,
      url: '/api/v1/portfolio/daily-detail',
      params: { id, d },
      onSuccess: (data) => setDetail((data as DailyDetail) ?? null),
      onError: () => setDetail(null),
    });
  }, [axiosInstance, m, logger]);

  // 계좌가 바뀌면 열려있던 상세는 닫는다
  useEffect(() => { setDetailD(null); setDetail(null); }, [pfId, d0, d1]);

  const selected = accounts.find(a => a.id === pfId);

  // ---- 계좌 통합 그리드 ----
  const acctCols = useMemo<ColDef<AccountRow>[]>(() => [
    { field: 'id', headerName: 'ID', width: 55, cellStyle: { textAlign: 'right' } },
    { field: 'htsId', headerName: 'HTS ID', width: 95 },
    { field: 'accountNo', headerName: '계좌번호', width: 115, cellStyle: { textAlign: 'center' } },
    { field: 'initD', headerName: '시작일자', width: 100, cellStyle: { textAlign: 'center' } },
    {
      field: 'userNm', headerName: '소유자', width: 90,
      valueGetter: (p) => p.data?.userNm || String(p.data?.userId ?? ''),
    },
    { field: 'initAmt', headerName: '초기금액', width: 120, cellStyle: { textAlign: 'right' }, valueFormatter: numFmt },
    {
      field: 'isReal', headerName: '구분', width: 65, cellStyle: (p) => ({
        textAlign: 'center', color: p.value ? '#dc2626' : '#2563eb', fontWeight: 600,
      }),
      valueFormatter: (p) => p.value ? '실전' : '모의',
    },
    {
      field: 'isActive', headerName: '상태', width: 70, cellStyle: (p) => ({
        textAlign: 'center', color: p.value ? '#16a34a' : '#dc2626', fontWeight: 600,
      }),
      valueFormatter: (p) => p.value ? '사용중' : '정지',
    },
    { field: 'days', headerName: '영업일수', width: 85, cellStyle: { textAlign: 'right' } },
    { field: 'baseNa', headerName: '기초순자산', width: 125, cellStyle: { textAlign: 'right' }, valueFormatter: numFmt },
    { field: 'lastNa', headerName: '기말순자산', width: 125, cellStyle: { textAlign: 'right' }, valueFormatter: numFmt },
    {
      field: 'totalRet', headerName: '누적수익률 (%)', width: 130,
      cellStyle: (p) => ({ textAlign: 'right', fontWeight: 700, color: sign(p.value) }),
      valueFormatter: pctFmt,
    },
    { field: 'winRate', headerName: '승률 (%)', width: 90, cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => p.value != null ? p.value.toFixed(1) : '' },
    {
      field: 'avgRet', headerName: '일평균 (%)', width: 105,
      cellStyle: (p) => ({ textAlign: 'right', color: sign(p.value) }), valueFormatter: pctFmt,
    },
    { field: 'maxRet', headerName: '최고 (%)', width: 100, cellStyle: { textAlign: 'right', color: '#dc2626' }, valueFormatter: pctFmt },
    { field: 'minRet', headerName: '최저 (%)', width: 100, cellStyle: { textAlign: 'right', color: '#2563eb' }, valueFormatter: pctFmt },
  ], []);

  // ---- 일별 그리드 ----
  const dailyCols = useMemo<ColDef<DailyRow>[]>(() => [
    { field: 'd', headerName: '일자', width: 105, cellStyle: { textAlign: 'center' } },
    { field: 'na', headerName: '순자산', width: 130, cellStyle: { textAlign: 'right' }, valueFormatter: numFmt },
    {
      field: 'diffAmt', headerName: '증감액', width: 120,
      cellStyle: (p) => ({ textAlign: 'right', color: sign(p.value) }), valueFormatter: numFmt,
    },
    {
      field: 'dailyRet', headerName: '일별수익률 (%)', width: 130,
      cellStyle: (p) => ({ textAlign: 'right', fontWeight: 600, color: sign(p.value) }), valueFormatter: pctFmt,
    },
    {
      field: 'cumRet', headerName: '누적수익률 (%)', width: 130,
      cellStyle: (p) => ({ textAlign: 'right', fontWeight: 600, color: sign(p.value) }), valueFormatter: pctFmt,
    },
    { field: 'stkEvluAmt', headerName: '주식평가금액', width: 130, cellStyle: { textAlign: 'right' }, valueFormatter: numFmt },
    {
      field: 'prftSumAmt', headerName: '평가손익', width: 120,
      headerTooltip: '보유 중인 종목의 미실현 손익. 전량청산한 날은 보유가 없어 0 이다.',
      cellStyle: (p) => ({ textAlign: 'right', color: sign(p.value) }), valueFormatter: numFmt,
    },
    {
      // 당일매매 손익 (2026-10-07 신설).
      //
      // 평가손익은 **보유분 미실현**이라, intraday 처럼 15:05 에 전량 청산하면
      // 장 끝나고는 늘 0 이다 — "오늘 얼마 벌었나"를 볼 수가 없었다.
      // 이 컬럼은 체결(매도대금 − 매수대금)에서 비용까지 뺀 **실현손익**이다.
      field: 'tradeNet', headerName: '당일매매손익', width: 135,
      headerTooltip: '체결 기준 실현손익 (매도대금 − 매수대금 − 거래비용). '
                   + '당일 전량청산한 날은 이 값이 그날 손익의 전부다.',
      cellStyle: (p) => ({ textAlign: 'right', fontWeight: 600, color: sign(p.value) }),
      valueFormatter: (p) => {
        if (p.value == null) return '-';
        // 이월 포지션이 있으면 미실현분이 빠져 있다 — 숫자만 보고 오해하지 않게 표시한다.
        const partial = p.data?.flat === false ? ' *' : '';
        return Math.round(Number(p.value)).toLocaleString() + partial;
      },
    },
    // 비용 3종 — 수수료 → 거래세 → 거래비용(합계). 서버 값만 표시, 역산하지 않는다.
    {
      field: 'fee', headerName: '수수료', width: 110,
      cellStyle: { textAlign: 'right', color: '#6b7280' },
      valueFormatter: (p) => p.value == null ? '-' : Math.round(Number(p.value)).toLocaleString(),
    },
    {
      field: 'tax', headerName: '거래세', width: 110,
      cellStyle: { textAlign: 'right', color: '#6b7280' },
      valueFormatter: (p) => p.value == null ? '-' : Math.round(Number(p.value)).toLocaleString(),
    },
    {
      // 거래비용 = 수수료 + 거래세 (kispfbs.texAmt)
      field: 'texAmt', headerName: '거래비용', width: 110,
      cellStyle: { textAlign: 'right', color: '#6b7280' },
      valueFormatter: (p) => p.value == null ? '-' : Math.round(Number(p.value)).toLocaleString(),
    },
  ], []);

  // ---- 세부내역: 종목별 포지션 ----
  const detailPosCols = useMemo<ColDef<DetailPos>[]>(() => [
    { field: 'prodId', headerName: '종목코드', width: 90, cellStyle: { textAlign: 'center' } },
    { field: 'prodNm', headerName: '종목명', width: 130 },
    { field: 'pos', headerName: '보유', width: 70, cellStyle: { textAlign: 'right' }, valueFormatter: numFmt },
    { field: 'avgPrc', headerName: '평단가', width: 100, cellStyle: { textAlign: 'right' }, valueFormatter: numFmt },
    { field: 'c', headerName: '종가', width: 100, cellStyle: { textAlign: 'right' }, valueFormatter: numFmt },
    { field: 'bAmt', headerName: '매입금액', width: 110, cellStyle: { textAlign: 'right' }, valueFormatter: numFmt },
    { field: 'evluAmt', headerName: '평가금액', width: 110, cellStyle: { textAlign: 'right' }, valueFormatter: numFmt },
    {
      field: 'prft', headerName: '평가손익', width: 110,
      cellStyle: (p) => ({ textAlign: 'right', fontWeight: 600, color: sign(p.value) }), valueFormatter: numFmt,
    },
    {
      field: 'evluRt', headerName: '수익률 (%)', width: 100,
      cellStyle: (p) => ({ textAlign: 'right', color: sign(p.value) }), valueFormatter: pctFmt,
    },
    {
      field: 'prftKrw', headerName: '실현손익', width: 110,
      cellStyle: (p) => ({ textAlign: 'right', color: sign(p.value) }),
      valueFormatter: (p) => p.value == null ? '-' : Math.round(Number(p.value)).toLocaleString(),
    },
    { field: 'bQty', headerName: '매수', width: 70, cellStyle: { textAlign: 'right' }, valueFormatter: numFmt },
    { field: 'sQty', headerName: '매도', width: 70, cellStyle: { textAlign: 'right' }, valueFormatter: numFmt },
    {
      field: 'fee', headerName: '수수료', width: 85, cellStyle: { textAlign: 'right', color: '#6b7280' },
      valueFormatter: (p) => p.value == null ? '-' : Math.round(Number(p.value)).toLocaleString(),
    },
    {
      field: 'tax', headerName: '거래세', width: 85, cellStyle: { textAlign: 'right', color: '#6b7280' },
      valueFormatter: (p) => p.value == null ? '-' : Math.round(Number(p.value)).toLocaleString(),
    },
  ], []);

  // ---- 세부내역: 체결 ----
  /**
   * 손익 집계 그리드 — **위 '일별 성과' 와 대조**가 목적이다.
   *
   * 일별 성과는 KIS 잔고(순자산 증감) 하나만 보고,
   * 이 표는 체결에서 자체 계산한 값 + KIS 값 + 그 차이를 함께 보여준다.
   * 같은 날짜 행을 두 표에서 비교하면 숫자가 맞는지 바로 보인다.
   */
  const pnlCols = useMemo<ColDef<PnlRow>[]>(() => [
    { field: 'd', headerName: '일자', width: 105, cellStyle: { textAlign: 'center' } },
    {
      field: 'nFill', headerName: '체결', width: 65,
      headerTooltip: '그날 체결 건수',
      cellStyle: { textAlign: 'right', color: '#6b7280' },
    },
    { field: 'buyAmt', headerName: '매수대금', width: 115, cellStyle: { textAlign: 'right' }, valueFormatter: numFmt },
    { field: 'sellAmt', headerName: '매도대금', width: 115, cellStyle: { textAlign: 'right' }, valueFormatter: numFmt },
    {
      field: 'realized', headerName: '체결손익', width: 110,
      headerTooltip: '매도대금 − 매수대금 (비용 전). 미청산일(이월 보유가 있는 날)은 이 값이 현금흐름이다 — 매수만 한 날은 매수금액만큼 음수가 된다. 손익이 아니다. 숫자 뒤 * 가 그 표시다.',
      // 미청산일은 손익이 아니라 현금흐름 — 색을 빼고 * 를 붙인다 (2026-10-11)
      cellStyle: (p) => (p.data?.flat === false
        ? { textAlign: 'right', color: '#94a3b8' }
        : { textAlign: 'right', color: sign(p.value) }),
      valueFormatter: (p) => (p.value == null ? '-'
        : Math.round(Number(p.value)).toLocaleString() + (p.data?.flat === false ? ' *' : '')),
    },
    {
      field: 'cost', headerName: '비용', width: 90,
      headerTooltip: '거래비용. kispfbs 의 texAmt / texAmtYd 중 값이 있는 쪽',
      cellStyle: { textAlign: 'right', color: '#6b7280' }, valueFormatter: numFmt,
    },
    {
      field: 'net', headerName: '자체계산 손익', width: 125,
      headerTooltip: '체결손익 − 비용. 당일 전량청산한 날의 실현손익이다. 미청산일(이월 보유가 있는 날)은 이 값이 현금흐름이다 — 매수만 한 날은 매수금액만큼 음수가 된다. 손익이 아니다. 숫자 뒤 * 가 그 표시다.',
      cellStyle: (p) => (p.data?.flat === false
        ? { textAlign: 'right', color: '#94a3b8' }
        : { textAlign: 'right', fontWeight: 700, color: sign(p.value) }),
      valueFormatter: (p) => (p.value == null ? '-'
        : Math.round(Number(p.value)).toLocaleString() + (p.data?.flat === false ? ' *' : '')),
    },
    {
      // KIS 가 **직접 준** 값 (asst_icdc_amt)
      field: 'kisDiffAmt', headerName: 'KIS 일손익', width: 115,
      headerTooltip: 'KIS 가 직접 내려준 일손익(asst_icdc_amt)',
      cellStyle: (p) => ({ textAlign: 'right', color: sign(p.value) }), valueFormatter: numFmt,
    },
    {
      // **검증의 핵심** — 우리 계산과 KIS 원본의 차이
      field: 'kisGap', headerName: 'KIS 차이', width: 95,
      headerTooltip: '자체계산 − KIS 원본. 0 이 아닌데 NAV검증이 0 이면 KIS 값이 틀린 것이다',
      valueFormatter: (p) => {
        if (p.value == null) return '-';
        const v = Math.round(Number(p.value));
        return v === 0 ? '0 ✓' : v.toLocaleString();
      },
      cellStyle: (p) => {
        if (p.value == null) return { textAlign: 'right', color: '#9ca3af' };
        const ok = Math.abs(Number(p.value)) < 1;
        return {
          textAlign: 'right', fontWeight: 700,
          color: ok ? '#16a34a' : '#b45309',
          backgroundColor: ok ? '#f0fdf4' : '#fffbeb',
        };
      },
    },
    {
      field: 'navDiff', headerName: '순자산증감', width: 110,
      headerTooltip: '당일 순자산 − 전일 순자산 (우리가 계산). 위 일별 성과의 증감액과 같은 값',
      cellStyle: (p) => ({ textAlign: 'right', color: sign(p.value) }), valueFormatter: numFmt,
    },
    {
      field: 'diffCheck', headerName: 'NAV검증', width: 90,
      headerTooltip: '자체계산 − 순자산증감. 0 이면 우리 집계가 잔고 움직임과 일치한다',
      valueFormatter: (p) => {
        if (p.value == null) return '-';
        const v = Math.round(Number(p.value));
        return v === 0 ? '0 ✓' : v.toLocaleString();
      },
      cellStyle: (p) => {
        if (p.value == null) return { textAlign: 'right', color: '#9ca3af' };
        const ok = Math.abs(Number(p.value)) < 1;
        return { textAlign: 'right', fontWeight: 600, color: ok ? '#16a34a' : '#b45309' };
      },
    },
    {
      field: 'flat', headerName: '청산', width: 80,
      headerTooltip: '당일 전량청산 여부. 미청산이면 이월분 미실현이 자체계산에 안 잡힌다',
      cellStyle: { textAlign: 'center' },
      valueFormatter: (p) => p.value ? '완료' : '미청산',
    },
    {
      field: 'unrealized', headerName: '평가손익', width: 105,
      headerTooltip: '보유분 미실현. 전량청산한 날은 0',
      cellStyle: (p) => ({ textAlign: 'right', color: sign(p.value) }), valueFormatter: numFmt,
    },
    {
      field: 'retPct', headerName: '수익률 (%)', width: 105,
      cellStyle: (p) => ({ textAlign: 'right', fontWeight: 600, color: sign(p.value) }),
      valueFormatter: pctFmt,
    },
    { field: 'nav', headerName: '순자산', width: 120, cellStyle: { textAlign: 'right' }, valueFormatter: numFmt },
    {
      field: 'updAt', headerName: '집계시각', width: 100,
      headerTooltip: 'GenPfBSJob 이 이 행을 마지막으로 계산한 시각 (5분 주기)',
      cellStyle: { textAlign: 'center', color: '#9ca3af' },
    },
  ], []);

  /** 종목별 당일매매 손익 — 일자 클릭 시 "무엇으로 벌고 잃었나" */
  const detailTradeCols = useMemo<ColDef<DetailTrade>[]>(() => [
    { field: 'prodId', headerName: '종목코드', width: 95, cellStyle: { textAlign: 'center' } },
    { field: 'prodNm', headerName: '종목명', minWidth: 140, flex: 1 },
    { field: 'bQty', headerName: '매수수량', width: 95, cellStyle: { textAlign: 'right' }, valueFormatter: numFmt },
    {
      field: 'bAvg', headerName: '매수평단', width: 105, cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => p.value ? Number(p.value).toFixed(2) : '-',
    },
    { field: 'sQty', headerName: '매도수량', width: 95, cellStyle: { textAlign: 'right' }, valueFormatter: numFmt },
    {
      field: 'sAvg', headerName: '매도평단', width: 105, cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => p.value ? Number(p.value).toFixed(2) : '-',
    },
    { field: 'bAmt', headerName: '매수대금', width: 115, cellStyle: { textAlign: 'right' }, valueFormatter: numFmt },
    { field: 'sAmt', headerName: '매도대금', width: 115, cellStyle: { textAlign: 'right' }, valueFormatter: numFmt },
    {
      field: 'pnl', headerName: '손익', width: 115,
      cellStyle: (p) => ({ textAlign: 'right', fontWeight: 700, color: sign(p.value) }),
      valueFormatter: numFmt,
    },
    {
      field: 'pnlRt', headerName: '수익률 (%)', width: 110,
      cellStyle: (p) => ({ textAlign: 'right', fontWeight: 600, color: sign(p.value) }),
      valueFormatter: pctFmt,
    },
    {
      // 매수수량 != 매도수량 이면 그날 안 끝난 거래다 — 손익이 일부만 실현됐다.
      field: 'flat', headerName: '청산', width: 80,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (p) => p.value ? '완료' : '미청산',
    },
  ], []);

  const detailFillCols = useMemo<ColDef<DetailFill>[]>(() => [
    { field: 'ordT', headerName: '체결시간', width: 90, cellStyle: { textAlign: 'center' } },
    { field: 'ordNo', headerName: '주문번호', width: 110, cellStyle: { textAlign: 'right' } },
    { field: 'prodId', headerName: '종목코드', width: 90, cellStyle: { textAlign: 'center' } },
    { field: 'prodNm', headerName: '종목명', width: 130 },
    {
      field: 'lsNm', headerName: '매도매수', width: 80,
      cellStyle: (p) => ({
        textAlign: 'center', fontWeight: 700,
        color: p.value === '매수' ? '#ef4444' : '#22c55e',
      }),
    },
    { field: 'totFillQty', headerName: '수량', width: 80, cellStyle: { textAlign: 'right' }, valueFormatter: numFmt },
    { field: 'avgPrc', headerName: '체결가', width: 110, cellStyle: { textAlign: 'right' }, valueFormatter: numFmt },
    { field: 'totFillAmt', headerName: '체결금액', width: 120, cellStyle: { textAlign: 'right', fontWeight: 600 }, valueFormatter: numFmt },
    {
      field: 'fee', headerName: '수수료', width: 85, cellStyle: { textAlign: 'right', color: '#6b7280' },
      valueFormatter: (p) => p.value == null ? '-' : Math.round(Number(p.value)).toLocaleString(),
    },
    {
      field: 'tax', headerName: '거래세', width: 85, cellStyle: { textAlign: 'right', color: '#6b7280' },
      valueFormatter: (p) => p.value == null ? '-' : Math.round(Number(p.value)).toLocaleString(),
    },
  ], []);

  const defaultColDef = useMemo<ColDef>(() => ({
    minWidth: 55, resizable: true, sortable: true, filter: false,
    cellStyle: { fontSize: '12px' },
  }), []);

  const chartOptions = useMemo<Highcharts.Options>(() => {
    const cum = rows.map(r => [new Date(r.d).getTime(), Number(r.cumRet.toFixed(4))]);
    const daily = rows.map(r => [new Date(r.d).getTime(), Number(r.dailyRet.toFixed(4))]);
    return {
      chart: { height: 300, zooming: { type: 'x' } },
      title: { text: undefined },
      credits: { enabled: false },
      xAxis: {
        type: 'datetime',
        labels: { formatter(this: any) { return axisDateLabel(this.value as number); } },
        dateTimeLabelFormats: {
          day: '%y.%m.%d', week: '%y.%m.%d', month: '%y.%m.%d', year: '%y.%m.%d',
        } as any,
      },
      yAxis: [
        { title: { text: '누적 (%)' }, labels: { align: 'right', x: -3 } },
        { title: { text: '일별 (%)' }, opposite: true, labels: { align: 'left', x: 3 } },
      ],
      tooltip: { shared: true, xDateFormat: '%y.%m.%d', valueDecimals: 4, valueSuffix: '%' },
      legend: { enabled: true },
      series: [
        { type: 'area', name: '누적수익률', data: cum, yAxis: 0, color: '#3b82f6',
          fillOpacity: 0.15, lineWidth: 2, marker: { enabled: false } },
        { type: 'column', name: '일별수익률', data: daily, yAxis: 1, color: '#f59e0b' },
      ],
    };
  }, [rows]);

  // 손익금액 = 기말순자산 - 기초순자산
  const pnlAmt = summary ? summary.lastNa - summary.baseNa : 0;

  return (
    <div style={{ padding: '20px', backgroundColor: '#eff6ff', minHeight: '100vh' }}>
      {/* 조회 조건 */}
      <div style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <span style={{ color: '#1e40af', fontSize: '12px', fontWeight: 600 }}>
          포트폴리오 복기 {loading ? '(조회 중...)' : `— 전체 ${accounts.length}계좌`}
        </span>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <DateInput value={d0} onChange={setD0} style={{ fontSize: '12px' }} />
          <span style={{ color: '#6b7280', fontSize: '12px' }}>~</span>
          <DateInput value={d1} onChange={setD1} style={{ fontSize: '12px' }} />
          <button onClick={fetchAll}
            style={{ padding: '5px 12px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '12px', fontWeight: 500 }}>
            조회
          </button>
        </div>
      </div>

      {/* 전체 계좌 (계좌정보 + 기간성과 통합) — 행 클릭 시 아래 차트/일별 갱신 */}
      <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
        <div style={titleRow}>
          <h3 style={gridTitle}>
            계좌 목록 <span style={gridHint}>(클릭: 일별 성과 조회)</span>
          </h3>
          <span style={{ color: '#1e40af', fontSize: '12px', fontWeight: 500 }}>
            총 {accounts.length}건
          </span>
        </div>
        <div className="ag-theme-quartz" style={{ height: 230, width: '100%' }}>
          <AgGridReact {...acctCopy.gridProps}
            ref={acctGridRef}
            rowData={accounts}
            columnDefs={acctCols}
            defaultColDef={defaultColDef}
            animateRows={false}
            rowSelection={'single' as any}
            onRowClicked={(e) => { if (e.data) setPfId(e.data.id); }}
            getRowStyle={(p) => p.data?.id === pfId
              ? { backgroundColor: '#dbeafe', fontWeight: 600 }
              : undefined}
          />
        </div>
      </div>
      {acctCopy.menu}

      {/* 선택 계좌 상세 */}
      <div style={{ ...card, marginBottom: '8px', padding: '8px 16px' }}>
        <span style={{ color: '#1e40af', fontSize: '12px', fontWeight: 600 }}>
          {selected ? (
            <>
              {selected.htsId || selected.accountNo || `pf${selected.id}`} / {selected.accountNo || '-'}{' '}
              <b style={{ color: selected.isReal ? '#dc2626' : '#2563eb' }}>
                {selected.isReal ? '실전' : '모의'}
              </b>
              {' · 일별수익률'}
            </>
          ) : '위 표에서 계좌를 클릭하면 일별수익률이 표시됩니다.'}
        </span>
        {summary && (
          <span style={{ color: '#6b7280', fontSize: '12px', marginLeft: 10 }}>
            {summary.firstD} ~ {summary.lastD} · {summary.days}일 · 누적{' '}
            <b style={{ color: sign(summary.totalRet) }}>{summary.totalRet.toFixed(4)}%</b> ·
            승률 {summary.winRate.toFixed(1)}% ({summary.winDays}/{summary.loseDays}) · 손익금액{' '}
            <b style={{ color: sign(pnlAmt) }}>{Math.round(pnlAmt).toLocaleString()}원</b>
          </span>
        )}
      </div>

      {/* 차트 */}
      <div style={card}>
        <h3 style={{ ...gridTitle, marginBottom: '8px' }}>
          수익률 추이 <span style={gridHint}>누적(영역) · 일별(막대)</span>
        </h3>
        {rows.length > 0
          ? <HighchartsReact highcharts={Highcharts} options={chartOptions} />
          : <div style={{ color: '#6b7280', fontSize: '12px', textAlign: 'center', padding: '40px 0' }}>
              데이터가 없습니다.
            </div>}
      </div>

      {/* 일별 그리드 */}
      <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
        <div style={titleRow}>
          <h3 style={gridTitle}>
            일별 성과 <span style={gridHint}>(일자 클릭: 세부내역)</span>
          </h3>
          <span style={{ color: '#1e40af', fontSize: '12px', fontWeight: 500 }}>
            총 {rows.length}건
          </span>
        </div>
        <div className="ag-theme-quartz" style={{ height: 360, width: '100%' }}>
          <AgGridReact {...dailyCopy.gridProps}
            rowData={rows}
            columnDefs={dailyCols}
            defaultColDef={defaultColDef}
            animateRows={false}
            onRowClicked={(e) => {
              if (!e.data || pfId == null) return;
              const d = e.data.d;
              if (detailD === d) { setDetailD(null); setDetail(null); return; } // 토글
              setDetailD(d);
              fetchDetail(pfId, d);
            }}
            getRowStyle={(p) => p.data?.d === detailD
              ? { backgroundColor: '#dbeafe', fontWeight: 600 }
              : undefined}
          />
        </div>
      </div>
      {dailyCopy.menu}

      {/* 손익 집계 — 위 '일별 성과' 와 **대조용**으로 나란히 둔다 (2026-10-08).
          일별 성과는 KIS 잔고만 보고, 이 표는 체결에서 자체 계산한 값까지 보여준다.
          같은 날짜 행을 두 표에서 비교하면 숫자가 맞는지 바로 보인다. */}
      <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
        <div style={titleRow}>
          <h3 style={gridTitle}>
            손익 집계 <span style={gridHint}>(체결로 자체 계산 ↔ KIS 잔고 대조 · GenPfBSJob 5분 주기)</span>
          </h3>
          {pnlRows.length > 0 && (() => {
            // 대조 결과 한 줄 요약 — 그리드를 훑지 않아도 상태가 보이게.
            const checked = pnlRows.filter(r => r.kisGap != null);
            const ok = checked.filter(r => Math.abs(r.kisGap ?? 0) < 1).length;
            const bad = checked.length - ok;
            return (
              <span style={{ fontSize: '12px', fontWeight: 500 }}>
                <span style={{ color: '#16a34a' }}>일치 {ok}일</span>
                {bad > 0 && <span style={{ color: '#b45309' }}> · 불일치 {bad}일</span>}
                <span style={{ color: '#9ca3af' }}> · 총 {pnlRows.length}건</span>
              </span>
            );
          })()}
        </div>
        <div className="ag-theme-quartz" style={{ height: 360, width: '100%' }}>
          <AgGridReact
            rowData={pnlRows}
            columnDefs={pnlCols}
            defaultColDef={defaultColDef}
            animateRows={false}
            overlayNoRowsTemplate="손익 집계 데이터 없음 (GenPfBSJob 실행 전이거나 그 기간 거래가 없음)"
            getRowStyle={(p) => p.data?.d === detailD
              ? { backgroundColor: '#dbeafe', fontWeight: 600 }
              : undefined}
          />
        </div>
      </div>

      {/* 일자 세부내역 — 일별 그리드에서 일자 클릭 시 */}
      {detailD && (
        <>
          {/* 종목별 당일매매 손익 — 전량청산하는 날은 포지션이 비어 이 표가 핵심이다 */}
          <div style={{ backgroundColor: 'white', border: '2px solid #8b5cf6', borderRadius: '8px', padding: '16px', marginBottom: '12px' }}>
            <div style={titleRow}>
              <h3 style={gridTitleSub}>당일매매 손익 ({detailD})</h3>
              {detail && (
                <span style={{ fontSize: '12px', fontWeight: 600,
                               color: (detail.tradePnlSum ?? 0) >= 0 ? '#16a34a' : '#dc2626' }}>
                  합계 {Math.round(detail.tradePnlSum ?? 0).toLocaleString()}원
                  <span style={{ color: '#6b7280', fontWeight: 400 }}>
                    {' '}(비용 전) · 수수료 {Math.round(detail.feeSum ?? 0).toLocaleString()}
                    {' '}· 거래세 {Math.round(detail.taxSum ?? 0).toLocaleString()}
                    {detail.tradeFlat ? ' · 전량청산' : ' · 이월분 있음(미실현 제외)'}
                  </span>
                </span>
              )}
            </div>
            <div className="ag-theme-quartz" style={{ height: 200, width: '100%' }}>
              <AgGridReact
                rowData={detail?.tradePnl ?? []}
                columnDefs={detailTradeCols}
                defaultColDef={defaultColDef}
                animateRows={false}
                overlayNoRowsTemplate="그날 체결이 없습니다"
              />
            </div>
          </div>

          {/* 체결 내역 → 보유종목 순 (PortfolioOverview 와 동일 순서) */}
          <div style={{ backgroundColor: 'white', border: '2px solid #8b5cf6', borderRadius: '8px', padding: '16px', marginBottom: '12px' }}>
            <div style={titleRow}>
              <h3 style={gridTitleSub}>체결 내역 ({detailD})</h3>
              {detail && (
                <span style={{ color: '#6d28d9', fontSize: '12px', fontWeight: 500 }}>
                  총 {detail.fills.length}건
                </span>
              )}
            </div>
            <div className="ag-theme-quartz" style={{ height: 220, width: '100%' }}>
              <AgGridReact
                rowData={detail?.fills ?? []}
                columnDefs={detailFillCols}
                defaultColDef={defaultColDef}
                animateRows={false}
                overlayNoRowsTemplate="체결내역이 없습니다"
              />
            </div>
          </div>

          <div style={{ backgroundColor: 'white', border: '2px solid #8b5cf6', borderRadius: '8px', padding: '16px' }}>
            <div style={titleRow}>
              <h3 style={gridTitleSub}>포지션 현황 ({detailD})</h3>
              {detail && (
                <span style={{ color: '#6d28d9', fontSize: '12px', fontWeight: 500 }}>
                  총 {detail.positions.length}건
                </span>
              )}
            </div>
            <div className="ag-theme-quartz" style={{ height: 240, width: '100%' }}>
              <AgGridReact
                rowData={detail?.positions ?? []}
                columnDefs={detailPosCols}
                defaultColDef={defaultColDef}
                animateRows={false}
                overlayNoRowsTemplate="보유종목이 없습니다"
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PortfolioReview;
